/**
 * Instrument metadata + Fyers option-symbol date encodings — extraction #3b.
 * Previously: the instrument table lived in static/paper.html, the
 * weekly/monthly expiry encoders were duplicated between paper.html and
 * routers/option_chain.py's build_option_symbol (whose docstring records the
 * live incident that motivates getting this EXACTLY right: using the monthly
 * format for a weekly expiry silently resolves to a DIFFERENT contract —
 * same-looking symbol, wrong instrument, wrong premium).
 */

export interface InstrumentInfo {
  value: string;
  label: string;
  base: string;
  title: string;
  exchange: "NSE" | "BSE";
  internal: string;
  /** Lot size (units per lot). */
  qty: number;
  /** Strike step in index points. */
  step: number;
}

export const symbolsByExchange: Record<"NSE" | "BSE", InstrumentInfo[]> = {
  NSE: [
    { value: "NIFTY", label: "Nifty", base: "NIFTY", title: "Nifty", exchange: "NSE", internal: "NSE_INDEX|Nifty 50", qty: 65, step: 50 },
    { value: "BANKNIFTY", label: "Bank Nifty", base: "BANKNIFTY", title: "Bank Nifty", exchange: "NSE", internal: "NSE_INDEX|Nifty Bank", qty: 30, step: 100 },
  ],
  BSE: [
    { value: "SENSEX", label: "Sensex", base: "SENSEX", title: "Sensex", exchange: "BSE", internal: "BSE_INDEX|SENSEX", qty: 20, step: 100 },
  ],
};

// ── market exchange <-> broker segment (ORDER-1) ─────────────────────────────
//
// TWO VOCABULARIES, and conflating them sent every SENSEX order to NFO.
//
//   MARKET EXCHANGE   "NSE" / "BSE"   part of the Fyers tradingsymbol
//                                     ("BSE:SENSEX2681377200PE")
//   BROKER SEGMENT    "NFO" / "BFO"   what Flattrade's place_order/search want
//
// Both were being carried in one field — `BasketLegIdentity.exchange`, documented
// as a market exchange. paper.html stored the market exchange there; live.html
// stored the broker segment (`optEl.dataset.exch`). Nothing reconciled them, so
// `leg.exchange === "BSE" ? "BFO" : "NFO"` was dead code that always chose NFO,
// and NIFTY was correct only by accident. SENSEX options trade on BFO, so every
// SENSEX order was rejected. Found live 2026-08-13 during expiry.
//
// These are the one place the mapping exists. Deriving it inline anywhere else is
// how two derivations diverged in the first place.

/** Market exchange -> the broker segment its options trade on. */
export const BROKER_SEGMENT: Record<string, string> = { NSE: "NFO", BSE: "BFO" };

/** Broker segment -> the market exchange it belongs to. */
export const MARKET_EXCHANGE: Record<string, string> = { NFO: "NSE", BFO: "BSE" };

/**
 * "NSE" -> "NFO", "BSE" -> "BFO". **"" for anything unrecognised.**
 *
 * The empty return is the point. The bug this replaces DEFAULTED to "NFO", so an
 * unrecognised value silently became a real, wrong, tradeable exchange. A caller
 * on the order path must treat "" as "refuse to place" — never as a fallback.
 * Accepts a value that is already a segment, so a leg persisted before this fix
 * still maps rather than being rejected.
 */
export function brokerSegment(exchange: string | null | undefined): string {
  const key = String(exchange || "").toUpperCase();
  if (MARKET_EXCHANGE[key]) return key;      // already a segment
  return BROKER_SEGMENT[key] || "";
}

/**
 * "NFO" -> "NSE", "BFO" -> "BSE". "" for anything unrecognised.
 *
 * Used where the value becomes part of a tradingsymbol. Also accepts a market
 * exchange unchanged, so this is safe to apply to a value of either vocabulary.
 */
export function marketExchange(segment: string | null | undefined): string {
  const key = String(segment || "").toUpperCase();
  if (BROKER_SEGMENT[key]) return key;       // already a market exchange
  return MARKET_EXCHANGE[key] || "";
}

/** Internal index key -> Fyers streaming symbol (mirrors feeds/fyers/feed.py). */
export const INTERNAL_TO_FYERS_INDEX: Record<string, string> = {
  "NSE_INDEX|Nifty 50": "NSE:NIFTY50-INDEX",
  "NSE_INDEX|Nifty Bank": "NSE:NIFTYBANK-INDEX",
  "BSE_INDEX|SENSEX": "BSE:SENSEX-INDEX",
};

export const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"] as const;

/** Fyers' single-character month code for WEEKLY symbols: 1-9, then O/N/D. */
export function fyersMonthChar(monthIndex0: number): string {
  if (isNaN(monthIndex0) || !isFinite(monthIndex0)) return "";
  const m = monthIndex0 + 1;
  if (m >= 1 && m <= 9) return String(m);
  return ({ 10: "O", 11: "N", 12: "D" } as Record<number, string>)[m] || "";
}

/**
 * Last weekly expiry of its calendar month = the monthly contract's date
 * (NSE runs no separately-dated monthly series). Pure date math instead of
 * a hardcoded weekday, so this survives NSE's periodic expiry-day changes.
 */
export function isMonthlyExpiry(d: Date): boolean {
  if (!d || isNaN(d.getTime())) return false;
  const next = new Date(d.getTime() + 7 * 24 * 60 * 60 * 1000);
  return next.getMonth() !== d.getMonth();
}

const MONTH_MAP: Record<string, number> = {
  JAN: 0, FEB: 1, MAR: 2, APR: 3, MAY: 4, JUN: 5,
  JUL: 6, AUG: 7, SEP: 8, OCT: 9, NOV: 10, DEC: 11,
};

/**
 * Robust date parser for expiry strings in ISO YYYY-MM-DD or Arrow DD-MMM-YYYY format.
 * Returns null if invalid or unparseable.
 */
export function parseExpiryDate(dateStr: string | null | undefined): Date | null {
  if (!dateStr || typeof dateStr !== "string") return null;
  const trimmed = dateStr.trim();
  if (!trimmed || trimmed === "undefined" || trimmed === "null") return null;

  // Format 1: DD-MMM-YYYY or D-MMM-YYYY (e.g. "08-SEP-2026", "8-Sep-2026")
  const ddMmm = trimmed.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/);
  if (ddMmm) {
    const day = parseInt(ddMmm[1], 10);
    const mStr = ddMmm[2].toUpperCase();
    const year = parseInt(ddMmm[3], 10);
    const month = MONTH_MAP[mStr];
    if (month !== undefined && day >= 1 && day <= 31) {
      const d = new Date(year, month, day);
      if (!isNaN(d.getTime())) return d;
    }
  }

  // Format 2: YYYY-MM-DD (e.g. "2026-09-08")
  const yyyyMmDd = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (yyyyMmDd) {
    const year = parseInt(yyyyMmDd[1], 10);
    const month = parseInt(yyyyMmDd[2], 10) - 1;
    const day = parseInt(yyyyMmDd[3], 10);
    const d = new Date(year, month, day);
    if (!isNaN(d.getTime())) return d;
  }

  // Format 3: fallback standard Date parsing
  const d = new Date(trimmed.includes("T") ? trimmed : trimmed + "T00:00:00");
  if (!isNaN(d.getTime())) return d;
  const dFallback = new Date(trimmed);
  return isNaN(dFallback.getTime()) ? null : dFallback;
}

/**
 * Fyers expiry code for an option/future symbol: monthly = YY+MMM
 * ("26JUL"); weekly = YY + month-char + DD ("26721" = 2026, July, 21st).
 * Returns "" if dateStr is invalid, empty, or undefined.
 */
export function formatExpiry(dateStr: string): string {
  const d = parseExpiryDate(dateStr);
  if (!d) return "";

  if (isMonthlyExpiry(d)) {
    const month = MONTHS[d.getMonth()];
    if (!month) return "";
    return String(d.getFullYear()).slice(-2) + month;
  }
  const yy = String(d.getFullYear()).slice(-2);
  const mChar = fyersMonthChar(d.getMonth());
  if (!mChar) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  return yy + mChar + dd;
}

/** Display form DDMMYY (e.g. "210726"). Returns "" if dateStr is invalid, empty, or undefined. */
export function formatExpiryDDMMYY(dateStr: string): string {
  const d = parseExpiryDate(dateStr);
  if (!d) return "";

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = String(d.getFullYear()).slice(-2);
  return day + month + year;
}

/** Full Fyers option symbol, e.g. ("NSE","NIFTY","2026-07-21",24250,"CE") -> "NSE:NIFTY2672124250CE".
 * Returns "" if any argument is missing, invalid, or produces a malformed symbol.
 */
export function buildOptionSymbol(
  exchange: string,
  base: string,
  expiryDateStr: string,
  strike: number | string,
  optType: "CE" | "PE",
): string {
  if (!exchange || !base || !expiryDateStr || strike === undefined || strike === null || strike === "" || !optType) {
    return "";
  }
  if (optType !== "CE" && optType !== "PE") {
    return "";
  }
  if (isNaN(Number(strike))) {
    return "";
  }
  const exp = formatExpiry(expiryDateStr);
  if (!exp) {
    return "";
  }
  return `${exchange}:${base}${exp}${strike}${optType}`;
}
